import React from "react";

/**
 * Portable 1-page A4 Cover Letter — no specific company name.
 * Designed to fit any AI / wellness / content / systems role.
 *
 * Exports:
 *   - PortableCoverSheet (named): used in /application-portable
 *   - PortableCoverLetter (default): standalone /cover-letter-portable page
 */

const CREAM = "#f4ede0";
const INK = "#1a1a1a";
const INK_SOFT = "#3a342b";
const BRONZE = "#8a6f47";
const RULE_SOFT = "rgba(26,26,26,0.10)";

const SERIF = '"Playfair Display", Georgia, "Times New Roman", serif';
const SANS = "Inter, -apple-system, BlinkMacSystemFont, sans-serif";

const Eyebrow = ({ children }) => (
    <p
        style={{
            fontFamily: SANS,
            color: BRONZE,
            fontSize: 9,
            letterSpacing: "0.36em",
            textTransform: "uppercase",
            fontWeight: 600,
            margin: 0,
        }}
    >
        {children}
    </p>
);

export const PortableCoverSheet = () => (
    <article
        className="cover-page"
        data-testid="cover-letter-sheet"
        style={{
            width: 794,
            height: 1120,
            maxWidth: "100%",
            margin: "0 auto",
            backgroundColor: CREAM,
            color: INK,
            boxShadow: "0 24px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(26,26,26,0.06)",
            padding: "56px 84px 44px",
            boxSizing: "border-box",
            position: "relative",
            fontFamily: SANS,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}
    >
        <header
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                paddingBottom: 18,
                borderBottom: `1px solid ${RULE_SOFT}`,
                marginBottom: 30,
            }}
        >
            <div>
                <h1
                    style={{
                        fontFamily: SERIF,
                        fontSize: 36,
                        fontWeight: 500,
                        color: INK,
                        margin: 0,
                        letterSpacing: "-0.02em",
                        lineHeight: 1,
                    }}
                >
                    Tatva K
                </h1>
                <p
                    style={{
                        fontFamily: SERIF,
                        fontStyle: "italic",
                        fontSize: 13,
                        color: BRONZE,
                        margin: "6px 0 0",
                    }}
                >
                    AI Systems Architect &middot; Audio-Visual Producer &middot;
                    Direct-Response Strategist
                </p>
            </div>
            <div style={{ textAlign: "right" }}>
                <Eyebrow>Dubai, UAE</Eyebrow>
                <p
                    style={{
                        fontFamily: SANS,
                        fontSize: 9.5,
                        color: INK_SOFT,
                        margin: "6px 0 0",
                        letterSpacing: "0.04em",
                        lineHeight: 1.55,
                    }}
                >
                    tatvak@theresetsanctuary.org
                    <br />
                    +971 50 676 9007
                    <br />
                    theresetsanctuary.org
                </p>
            </div>
        </header>

        <section
            style={{
                fontFamily: SERIF,
                fontSize: 12,
                lineHeight: 1.55,
                color: INK_SOFT,
                fontWeight: 400,
            }}
        >
            <p style={{ margin: "0 0 10px" }}>Dear hiring team,</p>

            <p style={{ margin: "0 0 10px" }}>
                I&rsquo;m writing to apply for your open role. The intersection of AI,
                content, and revenue is where I&rsquo;ve spent the last four years
                building from scratch, and I&rsquo;d love to bring that toolkit to your
                team.
            </p>

            <p style={{ margin: "0 0 10px" }}>
                A short introduction. I&rsquo;m a Berklee College of Music alumnus, a
                TEDx speaker on sound and cognition, and an Insight Timer Verified
                Teacher with{" "}
                <strong style={{ color: INK }}>
                    4,400+ hours of live sound practice
                </strong>{" "}
                behind me. Over the last twenty years I&rsquo;ve scaled audio
                properties to{" "}
                <strong style={{ color: INK }}>
                    150M+ verified organic streams
                </strong>{" "}
                and grown a personal distribution footprint of{" "}
                <strong style={{ color: INK }}>385K+ YouTube subscribers</strong> and{" "}
                <strong style={{ color: INK }}>130K+ Instagram followers</strong>{" "}
                across my work as a producer, broadcaster and founder.
            </p>

            <p style={{ margin: "0 0 10px" }}>
                What I bring beyond the craft is the systems mindset. For my own
                studio, The Reset Sanctuary in Dubai, I&rsquo;ve built and personally
                maintain a full content and marketing stack &mdash;{" "}
                <em style={{ color: BRONZE, fontStyle: "italic" }}>
                    FastAPI backend, Meta Conversions API, server-side Stripe
                    webhooks, automated MailerLite segmentation
                </em>{" "}
                &mdash; running at clean event-match quality and tied directly to
                revenue. Every piece of content I produce has a measurable outcome
                attached, not just engagement metrics.
            </p>

            <p style={{ margin: "0 0 10px" }}>
                On the creative side I work daily with: Claude and Emergent APIs for
                prompt logic, Kaiber AI for generative video, ElevenLabs for voice
                work, Final Cut Pro X for short-form editing, and the Meta Glasses
                Gen 2 ecosystem for multi-format capture. I run a daily content
                cadence, repurpose long-form podcasts and talks into short-form, and
                have been trained in the Andromeda framework under Jon Loomer&rsquo;s
                performance strategies. For copy I lean on Gary Halbert and Ben
                Settle direct-response frameworks &mdash; the kind that respects the
                reader and earns attention rather than chasing it.
            </p>

            <p style={{ margin: "0 0 10px" }}>
                One practical note: I run my own enterprise-grade AI infrastructure
                and pay for my private API access out of pocket, so I can hit the
                ground producing on day one without waiting on credential
                provisioning. From a single studio setup I&rsquo;m comfortably
                outputting at the volume of a small content team.
            </p>

            <p style={{ margin: "0 0 10px" }}>
                I&rsquo;m based in Dubai and available for full-time commitment. My
                own projects are team-supported, so I can give your team real focus
                from day one.
            </p>

            <p style={{ margin: "0 0 10px" }}>
                Everything I&rsquo;ve described is live at{" "}
                <em style={{ color: BRONZE, fontStyle: "italic" }}>
                    theresetsanctuary.org
                </em>{" "}
                and I&rsquo;d welcome a conversation whenever the timing suits.
            </p>

            <p style={{ margin: 0 }}>Thank you for the consideration.</p>
        </section>

        <div
            style={{
                marginTop: "auto",
                paddingTop: 20,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
            }}
        >
            <div>
                <p
                    style={{
                        fontFamily: SERIF,
                        fontStyle: "italic",
                        fontSize: 24,
                        color: INK,
                        margin: 0,
                        letterSpacing: "-0.01em",
                        lineHeight: 1,
                    }}
                >
                    With sound,
                </p>
                <p
                    style={{
                        fontFamily: SERIF,
                        fontSize: 20,
                        color: BRONZE,
                        margin: "8px 0 0",
                        fontWeight: 500,
                        letterSpacing: "-0.01em",
                    }}
                >
                    Tatva K
                </p>
            </div>
            <div style={{ textAlign: "right" }}>
                <Eyebrow>The Reset Sanctuary</Eyebrow>
                <p
                    style={{
                        fontFamily: SANS,
                        fontSize: 9.5,
                        color: INK_SOFT,
                        margin: "8px 0 0",
                        letterSpacing: "0.04em",
                        lineHeight: 1.6,
                    }}
                >
                    Dubai, UAE
                </p>
            </div>
        </div>
    </article>
);

const PortableCoverLetter = () => (
    <main
        data-testid="cover-letter-portable-page"
        style={{
            backgroundColor: "#2a2620",
            minHeight: "100vh",
            padding: "40px 16px 80px",
            fontFamily: SANS,
        }}
    >
        <div
            className="cover-toolbar"
            style={{
                maxWidth: 794,
                margin: "0 auto 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
            }}
        >
            <p
                style={{
                    color: "#c9a227",
                    fontSize: 11,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    margin: 0,
                    fontWeight: 600,
                }}
            >
                Tatva K · Portable Cover Letter
            </p>
        </div>
        <PortableCoverSheet />
    </main>
);

export default PortableCoverLetter;
